import { Link } from 'react-router-dom';
import { ArrowUpRight, BookOpen, Headphones, Landmark } from 'lucide-react';
import type { ReactNode } from 'react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero, LightSection } from '@/components/PageShell';
import { FundTheGapCTA } from '@/components/content/FundTheGapCTA';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';
import { LISTEN_READ_LAST_VERIFIED, books, organizations, podcasts, type CuratedItem } from '@/lib/data/listenAndRead';

const TITLE = 'Best Podcasts & Books for Sports Parents (2026)';
const DESCRIPTION =
  'Ten podcasts, ten books and the free organizations every youth-sports family should know — on development, mental skills, safety and what youth sports really cost.';

function itemList(name: string, items: CuratedItem[], type: 'PodcastSeries' | 'Book' | 'Organization') {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': type,
        name: it.name,
        ...(it.url ? { url: it.url } : {}),
        ...(it.by ? { [type === 'Book' ? 'author' : 'creator']: { '@type': 'Person', name: it.by } } : {}),
      },
    })),
  };
}

function Section({
  id,
  icon,
  title,
  lead,
  items,
  numbered,
}: {
  id: string;
  icon: ReactNode;
  title: string;
  lead: string;
  items: CuratedItem[];
  numbered?: boolean;
}) {
  return (
    <section className="mt-14" aria-labelledby={id}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700 grid place-items-center">{icon}</div>
        <h2 id={id} className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
          {title}
        </h2>
      </div>
      <p className="mt-2 text-slate-500">{lead}</p>
      <ol className="mt-6 space-y-5">
        {items.map((it, i) => (
          <li key={it.name} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-4">
              {numbered && (
                <span className="shrink-0 text-2xl font-extrabold text-yellow-500 tabular-nums w-8">{i + 1}</span>
              )}
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-slate-900 leading-snug">
                  {it.url ? (
                    <a href={it.url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-700 inline-flex items-center gap-1">
                      {it.name} <ArrowUpRight className="h-4 w-4 shrink-0" aria-hidden />
                    </a>
                  ) : (
                    it.name
                  )}
                </h3>
                {(it.by || it.meta) && (
                  <p className="text-sm text-slate-500">{[it.by, it.meta].filter(Boolean).join(' · ')}</p>
                )}
                <p className="mt-2 text-slate-700 leading-relaxed">{it.blurb}</p>
                {it.whyItMatters && (
                  <p className="mt-2 text-sm text-slate-600">
                    <span className="font-semibold text-slate-900">For sports families: </span>
                    {it.whyItMatters}
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function PodcastsAndBooks() {
  usePageMeta(TITLE, DESCRIPTION, {
    article: true,
    image: '/resources/coach-and-athlete.jpg',
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'Podcasts & books for sports parents' }],
    jsonLd: [
      itemList('Best podcasts for sports parents', podcasts, 'PodcastSeries'),
      itemList('Best books for sports parents', books, 'Book'),
      itemList('Free organizations for sports families', organizations, 'Organization'),
    ],
  });

  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Listen & read · Top picks"
          title={
            <>
              The best podcasts & books{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
                for sports parents.
              </span>
            </>
          }
          lead="For the drive to practice and the night before tryouts: shows and books on raising a happy athlete, mental skills, safety — and what youth sports really costs."
          size="sm"
        />
      }
    >
      <LightSection>
        <div className="max-w-3xl mx-auto">
          <p className="text-slate-600 leading-relaxed">
            We picked these for one test: would a parent paying for a club, a travel team or a nationals trip come away
            making better decisions? Every show has a real back catalogue (archives are marked), and nobody on this list
            paid to be here — there are no affiliate links.
          </p>
          <p className="mt-2 text-sm text-slate-500">Last reviewed {LISTEN_READ_LAST_VERIFIED}.</p>

          <Section
            id="podcasts"
            icon={<Headphones className="h-5 w-5" />}
            title="10 podcasts for sports parents and athletes"
            lead="Development, mindset and the youth-sports business, in roughly the order we'd start."
            items={podcasts}
            numbered
          />

          <Section
            id="books"
            icon={<BookOpen className="h-5 w-5" />}
            title="10 books worth reading"
            lead="Two on the money first — then development, mindset and the athlete's own head."
            items={books}
            numbered
          />

          <Section
            id="organizations"
            icon={<Landmark className="h-5 w-5" />}
            title="Free organizations every sports family should know"
            lead="Safety training, research and free courses — none of them cost a thing."
            items={organizations}
          />

          <div className="mt-14 rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <h2 className="text-xl font-bold text-slate-900">Know a show or book we missed?</h2>
            <p className="mt-2 text-slate-600">
              We're building this list with sports families.{' '}
              <Link to="/contact" className="text-blue-700 underline">
                Send us your pick
              </Link>{' '}
              — and if you're an athlete, coach or parent with a funding story to tell, we'd love to interview you for
              the Sport Fund blog.
            </p>
          </div>

          <FundTheGapCTA />

          <h2 className="mt-12 text-2xl font-extrabold tracking-tight text-slate-900">More from Resources</h2>
          <ul className="mt-4 space-y-2 text-blue-700">
            <li>
              <Link to="/resources/youth-sports-grants">Youth sports grants & financial aid directory</Link>
            </li>
            <li>
              <Link to="/resources/national-championships-calendar">Road to Nationals: championship calendar</Link>
            </li>
            <li>
              <Link to="/resources/fundraising-guides">Fundraising guides by sport</Link>
            </li>
          </ul>
          <ArticleFeedback articleSlug="podcasts-and-books-for-sports-parents" />
        </div>
      </LightSection>
    </PageShell>
  );
}
